/**
 * useGuidedTour — generalized Forecast/Releases step-machine hook.
 *
 * Consumers call `useGuidedTour({ mission })`, receive the current step plus
 * stable action callbacks, and render one <GuidedTour /> per page. All state
 * goes through the onboarding-state adapter; this hook never touches
 * localStorage directly.
 *
 * When `anchorHighlight` is true (default), the DOM element with `id` equal to
 * the current step's `anchorId` receives `atlas-tour-highlight` and
 * `forecast-tour-highlight` — same pulsing frame as Forecast/Releases tours.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  advanceMissionStep,
  completeMission,
  readMissionState,
  readMissionStep,
  restartMission,
  retreatMissionStep,
  skipMission,
} from '../adapters/onboardingStateAdapter';
import {
  TERMINAL_COMPLETED,
  TERMINAL_SKIPPED,
  type AtlasMission,
  type AtlasMissionStep,
} from '../atlas.types';
import { emitAtlas } from '../telemetry';
import { DOM_EVENT_ATLAS_MISSION_CHANGE } from '../../../shared/config/storageKeys';

/** Applied to the current step's `anchorId` element (must match index.css). */
export const ATLAS_TOUR_HIGHLIGHT_CLASSES = [
  'atlas-tour-highlight',
  'forecast-tour-highlight',
] as const;

/**
 * Notify `AtlasProvider` (and any other subscriber) that mission state changed
 * in this tab. Without this, the Atlas Dock shows a stale step because
 * `storage` events do not fire in the same tab that made the write.
 */
function notifyAtlasMissionChange(): void {
  try {
    window.dispatchEvent(new CustomEvent(DOM_EVENT_ATLAS_MISSION_CHANGE));
  } catch {
    // ignore - jsdom without CustomEvent etc.
  }
}

export interface UseGuidedTourResult {
  /** Fully hydrated — safe to render tour UI. */
  hydrated: boolean;
  /** The active step object, or `null` when the tour is idle/terminated. */
  step: AtlasMissionStep | null;
  stepIndex: number;
  totalSteps: number;
  isTerminal: boolean;
  /** Currently completed (step === 'completed' or legacy flag says so). */
  completed: boolean;
  /** Currently skipped (step === 'skipped'). */
  skipped: boolean;
  /** `true` when no-tour state is because org or product isn't selected yet. */
  missingContext: boolean;
  advance: () => void;
  retreat: () => void;
  skip: () => void;
  restart: () => void;
  /** Skip the current step forward (not used by default; provided for parity). */
  setStep: (stepId: string) => void;
}

function stepIndexOf(mission: AtlasMission, stepId: string | undefined): number {
  if (!stepId) return -1;
  return mission.steps.findIndex((s) => s.id === stepId);
}

function scrollToAnchor(anchorId: string | undefined): void {
  if (!anchorId || typeof window === 'undefined') return;
  window.requestAnimationFrame(() => {
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

export function useGuidedTour({
  mission,
  /**
   * When `false`, the tour will not auto-seed the welcome step even on first visit.
   * Useful when the page wants an external precondition (e.g. data loaded).
   */
  autoStart = true,
  /**
   * When `true`, the element `#${step.anchorId}` gets the pulsing tour outline.
   * Set `false` if the page mounts the hook without showing the coach (rare).
   */
  anchorHighlight = true,
  /**
   * When the anchor lives in a tab or lazy region, pass values that change when that
   * region becomes visible so the outline is re-applied (e.g. active tab id).
   */
  anchorExtraDeps = [] as unknown[],
}: {
  mission: AtlasMission;
  autoStart?: boolean;
  anchorHighlight?: boolean;
  anchorExtraDeps?: unknown[];
}): UseGuidedTourResult {
  const { currentOrg, currentProduct } = useAuth();
  const orgId = currentOrg?.id ?? null;
  const productId = currentProduct?.id ?? null;
  const missingContext = !orgId || !productId;

  const [rawStep, setRawStep] = useState<string | undefined>(undefined);
  const [hydrated, setHydrated] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const lastHighlightedEl = useRef<HTMLElement | null>(null);

  const clearAnchorHighlight = useCallback(() => {
    const el = lastHighlightedEl.current;
    if (el) {
      ATLAS_TOUR_HIGHLIGHT_CLASSES.forEach((c) => el.classList.remove(c));
      lastHighlightedEl.current = null;
    }
  }, []);

  // Hydrate on org/product change.
  useEffect(() => {
    if (missingContext || !orgId || !productId) {
      setRawStep(undefined);
      setHydrated(true);
      return;
    }
    const existing = readMissionStep(mission, orgId, productId);
    // Respect derived/legacy completion signals (e.g. a `featureKey` coach flag
    // set without a tour step) so we don't re-seed welcome for users who
    // already finished a tour before Atlas owned this storage.
    const state = readMissionState(mission, orgId, productId);
    const alreadyDone = state.completed || state.skipped;
    if (existing === undefined && !alreadyDone && autoStart && mission.steps.length > 0) {
      const first = mission.steps[0].id;
      // Seed the first step so a reload resumes at welcome rather than re-asking.
      restartMission(mission, orgId, productId);
      setRawStep(first);
      setStartTime(Date.now());
      emitAtlas('atlas.mission.started', { missionId: mission.id, orgId, productId });
      notifyAtlasMissionChange();
    } else {
      setRawStep(
        existing ??
          (alreadyDone ? (state.skipped ? TERMINAL_SKIPPED : TERMINAL_COMPLETED) : undefined)
      );
      if (existing && existing !== TERMINAL_COMPLETED && existing !== TERMINAL_SKIPPED) {
        setStartTime((prev) => prev ?? Date.now());
      }
    }
    setHydrated(true);
  }, [mission, orgId, productId, autoStart, missingContext]);

  const isTerminal = rawStep === TERMINAL_COMPLETED || rawStep === TERMINAL_SKIPPED;
  const activeStep: AtlasMissionStep | null = useMemo(() => {
    if (missingContext || isTerminal || !rawStep) return null;
    return mission.steps.find((s) => s.id === rawStep) ?? null;
  }, [mission.steps, rawStep, missingContext, isTerminal]);

  // Pulse + outline on the step target (same visual language as Forecast/Releases).
  useEffect(() => {
    clearAnchorHighlight();
    if (!anchorHighlight || missingContext || isTerminal || !activeStep?.anchorId) {
      return;
    }
    const anchorId = activeStep.anchorId;
    scrollToAnchor(anchorId);
    const apply = () => {
      const el = document.getElementById(anchorId);
      if (!el) return;
      if (lastHighlightedEl.current && lastHighlightedEl.current !== el) {
        ATLAS_TOUR_HIGHLIGHT_CLASSES.forEach((c) => lastHighlightedEl.current?.classList.remove(c));
      }
      ATLAS_TOUR_HIGHLIGHT_CLASSES.forEach((c) => el.classList.add(c));
      lastHighlightedEl.current = el;
    };
    apply();
    const t1 = window.setTimeout(apply, 120);
    const t2 = window.setTimeout(apply, 480);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      clearAnchorHighlight();
    };
  }, [
    anchorHighlight,
    activeStep?.anchorId,
    missingContext,
    isTerminal,
    rawStep,
    clearAnchorHighlight,
    ...anchorExtraDeps,
  ]);

  const stepIndex = stepIndexOf(mission, rawStep);
  const totalSteps = mission.steps.length;

  const advance = useCallback(() => {
    if (!orgId || !productId) return;
    const next = advanceMissionStep(mission, orgId, productId);
    setRawStep(next);
    if (next && next !== TERMINAL_COMPLETED && next !== TERMINAL_SKIPPED) {
      const stepObj = mission.steps.find((s) => s.id === next);
      scrollToAnchor(stepObj?.anchorId);
      emitAtlas('atlas.step.completed', {
        missionId: mission.id,
        stepId: next,
        orgId,
        productId,
      });
    } else if (next === TERMINAL_COMPLETED) {
      emitAtlas('atlas.mission.completed', {
        missionId: mission.id,
        orgId,
        productId,
        durationMs: startTime ? Date.now() - startTime : undefined,
      });
    }
    notifyAtlasMissionChange();
  }, [mission, orgId, productId, startTime]);

  const retreat = useCallback(() => {
    if (!orgId || !productId) return;
    const prev = retreatMissionStep(mission, orgId, productId);
    setRawStep(prev);
    if (prev && prev !== TERMINAL_COMPLETED && prev !== TERMINAL_SKIPPED) {
      const stepObj = mission.steps.find((s) => s.id === prev);
      scrollToAnchor(stepObj?.anchorId);
    }
    notifyAtlasMissionChange();
  }, [mission, orgId, productId]);

  const skip = useCallback(() => {
    if (!orgId || !productId) return;
    skipMission(mission, orgId, productId);
    setRawStep(TERMINAL_SKIPPED);
    emitAtlas('atlas.mission.skipped', { missionId: mission.id, orgId, productId });
    notifyAtlasMissionChange();
  }, [mission, orgId, productId]);

  const restart = useCallback(() => {
    if (!orgId || !productId) return;
    const first = restartMission(mission, orgId, productId);
    setRawStep(first);
    setStartTime(Date.now());
    if (first) {
      const stepObj = mission.steps.find((s) => s.id === first);
      scrollToAnchor(stepObj?.anchorId);
    }
    emitAtlas('atlas.mission.started', {
      missionId: mission.id,
      orgId,
      productId,
      reason: 'restart',
    });
    notifyAtlasMissionChange();
  }, [mission, orgId, productId]);

  const setStep = useCallback(
    (stepId: string) => {
      if (!orgId || !productId) return;
      const valid = mission.steps.some((s) => s.id === stepId);
      if (!valid) return;
      // Reuse the adapter directly via restart+advance dance? Simpler: call writeMissionStep.
      // Since writeMissionStep is internal to the adapter module we expose restart+advance here.
      // For now, navigate through advance() repeatedly would be overkill; instead, compute target
      // by using restart + advance until we hit it. To avoid that cost, we allow a direct write
      // through the adapter: mission + index mapping.
      const idx = mission.steps.findIndex((s) => s.id === stepId);
      if (idx === -1) return;
      // Walk: if terminal or different, reset to first then advance (idx times).
      restartMission(mission, orgId, productId);
      for (let i = 0; i < idx; i++) {
        advanceMissionStep(mission, orgId, productId);
      }
      setRawStep(stepId);
      const stepObj = mission.steps[idx];
      scrollToAnchor(stepObj?.anchorId);
      notifyAtlasMissionChange();
    },
    [mission, orgId, productId]
  );

  const complete = useCallback(() => {
    if (!orgId || !productId) return;
    completeMission(mission, orgId, productId);
    setRawStep(TERMINAL_COMPLETED);
    emitAtlas('atlas.mission.completed', {
      missionId: mission.id,
      orgId,
      productId,
      durationMs: startTime ? Date.now() - startTime : undefined,
      reason: 'manual',
    });
    notifyAtlasMissionChange();
  }, [mission, orgId, productId, startTime]);

  // Expose complete via restart-alias export to avoid signature churn; omit from public API
  // for now (not needed by Phase 1 pages).
  void complete;

  return {
    hydrated,
    step: activeStep,
    stepIndex,
    totalSteps,
    isTerminal,
    completed: rawStep === TERMINAL_COMPLETED,
    skipped: rawStep === TERMINAL_SKIPPED,
    missingContext,
    advance,
    retreat,
    skip,
    restart,
    setStep,
  };
}
